import React, { createContext, useState, useEffect } from 'react';
import {
  doc,
  collection,
  query,
  onSnapshot,
  getDoc,
  updateDoc,
} from 'firebase/firestore';
import { db } from './firebaseConfig';
import { useAuth } from './auth/AuthProvider';
import { listMyFarmsClient } from './utils/listMyFarmsClient';

export const AppContext = createContext();

export function AppProvider({ children }) {
  // Global states
  const { user, authReady, signOut } = useAuth();
  const [userData, setUserData] = useState(null);
  const [farmData, setFarmData] = useState(null);
  const [darkMode, setDarkMode] = useState(false);

  // Shared collections
  const [customers, setCustomers] = useState([]);
  const [crops, setCrops] = useState([]);
  const [seeds, setSeeds] = useState([]);
  // NEW: trays state
  const [trays, setTrays] = useState([]);
  const [media, setMedia] = useState([]);
  const [solutions, setSolutions] = useState([]);
  const [harvests, setHarvests] = useState([]);
  const [orders, setOrders] = useState([]);
  const [tasks, setTasks] = useState([]);
  // NEW: Add "invoices" state
  const [invoices, setInvoices] = useState([]);
  const activeFarmId = userData?.activeFarmId || '';

  // 1) If user is logged in, load userData + subcollections in one place
  useEffect(() => {
    if (!user) {
      // Clear data if user logs out
      setUserData(null);
      setFarmData(null);
      setCustomers([]);
      setCrops([]);
      setSeeds([]);
      setTrays([]);
      setMedia([]);
      setSolutions([]);
      setHarvests([]);
      setOrders([]);
      setTasks([]);
      setInvoices([]);
      return;
    }

    // Fetch user doc
    const userDocRef = doc(db, 'users', user.uid);
    const unsubscribeUser = onSnapshot(userDocRef, (docSnap) => {
      setUserData(docSnap.exists() ? docSnap.data() : null);
    });

    // User-scoped subcollections
    const unsubscribeCrops = onSnapshot(
      query(collection(db, 'users', user.uid, 'crops')),
      (snapshot) => {
        setCrops(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
      }
    );

    // Cleanup on unmount or user change
    return () => {
      unsubscribeUser();
      unsubscribeCrops();
    };
  }, [user]);

  // Farm-scoped Phase 1 collections
  useEffect(() => {
    if (!user || !activeFarmId) {
      setCustomers([]);
      setSeeds([]);
      setTrays([]);
      setMedia([]);
      setSolutions([]);
      setHarvests([]);
      setTasks([]);
      setOrders([]);
      setInvoices([]);
      return;
    }

    const unsubscribeCustomers = onSnapshot(
      query(collection(db, 'farms', activeFarmId, 'customers')),
      (snapshot) => {
        setCustomers(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
      }
    );

    const unsubscribeSeeds = onSnapshot(
      query(collection(db, 'farms', activeFarmId, 'seeds')),
      (snapshot) => {
        setSeeds(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
      }
    );

    const unsubscribeTrays = onSnapshot(
      query(collection(db, 'farms', activeFarmId, 'trays')),
      (snapshot) => {
        setTrays(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
      }
    );

    const unsubscribeSolutions = onSnapshot(
      query(collection(db, 'farms', activeFarmId, 'solutions')),
      (snapshot) => {
        setSolutions(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
      }
    );

    const unsubscribeMedia = onSnapshot(
      query(collection(db, 'farms', activeFarmId, 'media')),
      (snapshot) => {
        setMedia(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
      }
    );

    const unsubscribeHarvests = onSnapshot(
      query(collection(db, 'farms', activeFarmId, 'harvests')),
      (snapshot) => {
        setHarvests(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
      }
    );

    const unsubscribeTasks = onSnapshot(
      query(collection(db, 'farms', activeFarmId, 'tasks')),
      (snapshot) => {
        setTasks(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
      }
    );

    const unsubscribeOrders = onSnapshot(
      query(collection(db, 'farms', activeFarmId, 'orders')),
      (snapshot) => {
        setOrders(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
      }
    );

    const unsubscribeInvoices = onSnapshot(
      query(collection(db, 'farms', activeFarmId, 'invoices')),
      (snapshot) => {
        setInvoices(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
      }
    );

    return () => {
      unsubscribeCustomers();
      unsubscribeSeeds();
      unsubscribeTrays();
      unsubscribeSolutions();
      unsubscribeMedia();
      unsubscribeHarvests();
      unsubscribeTasks();
      unsubscribeOrders();
      unsubscribeInvoices();
    };
  }, [user, activeFarmId]);

  useEffect(() => {
    if (!userData?.activeFarmId) {
      setFarmData(null);
      return;
    }

    const farmDocRef = doc(db, 'farms', userData.activeFarmId);
    const unsubscribeFarm = onSnapshot(farmDocRef, (docSnap) => {
      setFarmData(docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } : null);
    });

    return () => {
      unsubscribeFarm();
    };
  }, [userData?.activeFarmId]);

  useEffect(() => {
    if (!user?.uid || !userData?.activeFarmId) {
      return undefined;
    }

    const activeMemberRef = doc(db, 'farms', userData.activeFarmId, 'members', user.uid);
    const unsubscribeMember = onSnapshot(activeMemberRef, async (memberSnap) => {
      if (memberSnap.exists()) {
        return;
      }

      try {
        const farms = await listMyFarmsClient();
        const nextFarmId = farms[0]?.id || '';

        const userRef = doc(db, 'users', user.uid);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          await updateDoc(userRef, { activeFarmId: nextFarmId });
        }
      } catch (error) {
        console.warn('Failed to recover activeFarmId after membership removal:', error);
      }
    });

    return () => unsubscribeMember();
  }, [user?.uid, userData?.activeFarmId]);

  // 3) Handle logout
  const handleLogout = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  // Expose everything to the rest of the app
  const contextValue = {
    user,
    userData,
    farmData,
    loading: !authReady,
    darkMode,
    setDarkMode,
    customers,
    crops,
    seeds,
    trays, // Added trays here
    media,
    solutions,
    harvests,
    orders,
    tasks,
    invoices,
    handleLogout,
  };

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
}

